import { AutocompleteV2Props, Box, Stack } from '@cast/design-system';
import { buildDboRoute, DboRoute } from 'features/db-optimization/v2/routes';

import {
  useCurrentOrganizationId,
  useNavigate,
} from '@cast/infrastructure-organization';

import { SidebarSection } from 'main-layout-op/_components/sidebar/SecondarySidebar/_components/SidebarSection';
import { DbServerSelect } from './DbServerSelect';

import { AxiosError } from 'axios';
import { useDboParams } from 'features/db-optimization/v2/hooks/useDboParams';
import { InstanceViewModelV2 } from 'features/db-optimization/v2/types';
import { useVisibleDboNavItems } from 'pages/db-optimization/v2/DbOptimizationControllerV2/utils';
import { DboSidebarItem } from './DboSidebarItem';

type ServerNavItemsProps = {
  instances: InstanceViewModelV2[];
  isLoading: boolean;
  error: AxiosError<unknown, any> | null;
  isOptimized: boolean;
  activeServer?: InstanceViewModelV2;
};

export const ServerNavItems = ({
  instances,
  isLoading,
  activeServer,
  error,
  isOptimized,
}: ServerNavItemsProps) => {
  const navigate = useNavigate();
  const { dbServerId } = useDboParams();
  const organizationId = useCurrentOrganizationId();

  const handleServerChange: AutocompleteV2Props<InstanceViewModelV2>['onChange'] =
    ({ value: newDbServerId }) => {
      if (!newDbServerId) {
        return;
      }
      navigate(
        buildDboRoute(DboRoute.SERVER_DASHBOARD, { dbServerId: newDbServerId })
      );
    };

  const serverParams = dbServerId ? { dbServerId } : null;

  const visibleItems = useVisibleDboNavItems('server', {
    isOptimized,
    isManualProvider: activeServer?.provider === 'MANUAL',
  });

  return (
    <SidebarSection title="Database server">
      <Box px={4} mb={8}>
        <DbServerSelect
          placeholder={
            isLoading
              ? 'Loading...'
              : error
              ? 'Error loading servers'
              : !instances.length
              ? 'No servers available'
              : 'Select database server'
          }
          instances={instances}
          value={dbServerId}
          onChange={handleServerChange}
        />
      </Box>

      {!!activeServer && serverParams && (
        <Stack gap="4px">
          {visibleItems.map((item) => (
            <DboSidebarItem
              key={`${item.title}-${item.route ?? ''}`}
              params={serverParams}
              item={item}
              organizationId={organizationId}
            />
          ))}
        </Stack>
      )}
    </SidebarSection>
  );
};
